import { RefreshCw } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { formatDateTime } from '~/utils/date';

import { useWorkflowChildren } from '../../hooks/use-workflow-inspection';
import type { WorkflowChild, WorkflowExecutionDetail } from '../../types/workflow-inspection.types';

interface Props {
  detail: WorkflowExecutionDetail;
  /** Renders a nested inspector for a child workflow (passed in to avoid an import cycle). */
  renderChildInspector: (workflowId: string, runId: string | null) => ReactNode;
  /** Host-specific relations shown above Temporal's own (e.g. a delivery's retry chain). */
  extra?: ReactNode;
}

/**
 * Relationships: the host's own relations (`extra`, e.g. retry lineage), this
 * execution's parent/root, and the child workflows it started (§3.5). A child
 * opens in a side sheet with its own inspector — the API resolves the child's
 * ownership through its parent.
 */
export function RelationshipsTab({ detail, renderChildInspector, extra }: Props) {
  const { t } = useTranslation('workflow-inspection');
  const [liveStatus, setLiveStatus] = useState(false);
  const [openChild, setOpenChild] = useState<WorkflowChild | null>(null);
  const children = useWorkflowChildren(detail.workflowId, detail.runId, {
    includeLiveStatus: liveStatus,
  });

  const refs = [
    { label: t('relationships.parent'), ref: detail.parent },
    // Root equal to self carries no information.
    {
      label: t('relationships.root'),
      ref: detail.root?.workflowId === detail.workflowId ? null : detail.root,
    },
  ].filter((r) => r.ref);

  return (
    <div className="space-y-5" data-testid="workflow-inspector-relationships">
      {extra}

      {refs.length > 0 && (
        <dl
          className="bg-background grid gap-3 rounded-lg border p-4 text-sm sm:grid-cols-2"
          data-testid="workflow-inspector-relationships-refs"
        >
          {refs.map(({ label, ref }) => (
            <div key={label} className="min-w-0">
              <dt className="text-muted-foreground text-xs">{label}</dt>
              <dd className="text-foreground font-mono text-xs break-all">{ref?.workflowId}</dd>
            </div>
          ))}
        </dl>
      )}

      <section className="space-y-2" data-testid="workflow-inspector-relationships-children">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-foreground text-sm font-semibold">{t('relationships.children')}</h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="cursor-pointer gap-1.5"
            isLoading={children.isFetching}
            data-testid="workflow-inspector-relationships-refresh-live-button"
            onClick={() => {
              if (liveStatus) void children.refetch();
              else setLiveStatus(true);
            }}
          >
            <RefreshCw aria-hidden="true" className="size-3.5" />
            {t('relationships.refreshLive')}
          </Button>
        </div>
        {children.isLoading ? (
          <Skeleton
            className="h-24 w-full"
            data-testid="workflow-inspector-relationships-children-loading"
          />
        ) : children.isError ? (
          <p
            className="text-muted-foreground text-sm"
            data-testid="workflow-inspector-relationships-children-error"
          >
            {t('errors.unavailable')}
          </p>
        ) : (children.data?.children.length ?? 0) === 0 ? (
          <p
            className="text-muted-foreground rounded-lg border p-6 text-center text-sm"
            data-testid="workflow-inspector-relationships-children-empty"
          >
            {t('relationships.noChildren')}
          </p>
        ) : (
          <div className="bg-background rounded-lg border">
            <Table data-testid="workflow-inspector-relationships-children-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('summary.workflowType')}</TableHead>
                  <TableHead>{t('history.table.status')}</TableHead>
                  <TableHead>{t('summary.started')}</TableHead>
                  <TableHead>{t('summary.closed')}</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {children.data?.children.map((child) => (
                  <TableRow
                    key={`${child.workflowId}:${child.runId ?? ''}`}
                    data-testid={`workflow-inspector-relationships-child-row-${child.workflowId}`}
                  >
                    <TableCell className="text-sm">
                      <span className="text-foreground font-medium">
                        {child.workflowType ?? '—'}
                      </span>
                      <span className="text-muted-foreground block font-mono text-xs break-all">
                        {child.workflowId}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {t(`status.${child.status}`, { defaultValue: child.status })}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {child.startedAt ? formatDateTime(child.startedAt) : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {child.closedAt ? formatDateTime(child.closedAt) : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto cursor-pointer p-0"
                        onClick={() => {
                          setOpenChild(child);
                        }}
                        data-testid={`workflow-inspector-relationships-child-inspect-button-${child.workflowId}`}
                      >
                        {t('relationships.inspect')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <Sheet
        open={openChild !== null}
        onOpenChange={(open) => {
          if (!open) setOpenChild(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-5xl"
          data-testid="workflow-inspector-relationships-child-sheet"
        >
          {openChild && (
            <>
              <SheetHeader>
                <SheetTitle>
                  {t('relationships.childTitle')} · {openChild.workflowType ?? openChild.workflowId}
                </SheetTitle>
              </SheetHeader>
              <div className="px-4 pb-6">
                {renderChildInspector(openChild.workflowId, openChild.runId)}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
